import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';

/*
 * Minimal typing of the Web NFC API (Chrome on Android, HTTPS only).
 * https://w3c.github.io/web-nfc/
 */
interface NdefRecord {
  recordType: string;
  data?: DataView;
}
interface NdefReadingEvent extends Event {
  serialNumber: string;
  message: { records: NdefRecord[] };
}
interface NdefReader extends EventTarget {
  scan(options?: { signal?: AbortSignal }): Promise<void>;
  onreading: ((event: NdefReadingEvent) => void) | null;
  onreadingerror: ((event: Event) => void) | null;
}
type NdefReaderConstructor = new () => NdefReader;

/** The same NFC tag is read several times while the phone rests on it. */
const SAME_TAG_PAUSE_MS = 3000;

/**
 * Extracts the tag id from a reading: the `/tag/<id>` path of the URL stored
 * on the NFC tag (the link the NFC tags have always carried), otherwise the
 * hardware serial number in the API format (`04:a1:...` -> `04-A1-...`).
 *
 * @returns The tag id, or null if the reading contains neither.
 */
export function tagIdFromReading(records: NdefRecord[], serialNumber: string): string | null {
  for (const record of records) {
    if ((record.recordType === 'url' || record.recordType === 'absolute-url') && record.data) {
      try {
        const url = new URL(new TextDecoder().decode(record.data));
        const match = url.pathname.match(/^\/tag\/([^/]+)\/?$/);
        if (match) return decodeURIComponent(match[1]);
      } catch {
        // not a URL - fall back to the serial number
      }
    }
  }
  return serialNumber ? serialNumber.toUpperCase().replaceAll(':', '-') : null;
}

/**
 * "Scan mode": reads NFC tags inside the already open tab instead of letting
 * the phone open a new tab for every NFC tag. Each NFC tag opens its tag page in
 * this tab (the tag page forwards bank and employment office users to their
 * page). Only available where the browser supports Web NFC (Chrome on
 * Android); must be started by a tap (browser permission).
 */
@Injectable({ providedIn: 'root' })
export class NfcScanService {
  private readonly router = inject(Router);
  private abort: AbortController | null = null;
  private last = { id: '', at: 0 };

  /** Web NFC is available in this browser. */
  readonly supported = typeof window !== 'undefined' && 'NDEFReader' in window;
  readonly active = signal(false);
  /** Why scanning could not be started or an NFC tag could not be read. */
  readonly error = signal<string | null>(null);

  /** Starts scanning (call from a click handler). */
  async start(): Promise<void> {
    if (!this.supported || this.active()) return;
    this.error.set(null);
    const Reader = (window as unknown as { NDEFReader: NdefReaderConstructor }).NDEFReader;
    const reader = new Reader();
    const abort = new AbortController();
    try {
      await reader.scan({ signal: abort.signal });
    } catch (error) {
      const name = (error as DOMException)?.name;
      this.error.set(name === 'NotAllowedError'
        ? 'Der Zugriff auf NFC wurde nicht erlaubt. Bitte in den Website-Einstellungen erlauben.'
        : name === 'NotReadableError'
          ? 'NFC ist ausgeschaltet. Bitte in den Einstellungen des Handys einschalten.'
          : 'Das Scannen konnte nicht gestartet werden.');
      return;
    }
    reader.onreading = (event) => this.onReading(event);
    reader.onreadingerror = () => this.error.set('Der NFC-Tag konnte nicht gelesen werden. Bitte erneut ans Handy halten.');
    this.abort = abort;
    this.active.set(true);
  }

  /** Stops scanning (e.g. on logout). */
  stop(): void {
    this.abort?.abort();
    this.abort = null;
    this.active.set(false);
  }

  private onReading(event: NdefReadingEvent): void {
    const id = tagIdFromReading(event.message?.records ?? [], event.serialNumber);
    if (id && this.openTag(id)) navigator.vibrate?.(80);
  }

  /**
   * Opens the page of a scanned NFC tag in this tab - used by the Web NFC scan
   * mode and by USB readers (`KeyboardScanService`). Repeated reads of the
   * same NFC tag within a few seconds are ignored.
   *
   * @returns false when the read was ignored as a repetition.
   */
  openTag(id: string): boolean {
    const now = Date.now();
    if (id === this.last.id && now - this.last.at < SAME_TAG_PAUSE_MS) return false;
    this.last = { id, at: now };
    this.error.set(null);
    // `scan` makes a repeated read of the same NFC tag load the page again
    void this.router.navigate(['/tag', id], { queryParams: { scan: String(now) } });
    return true;
  }
}
